import React, { lazy } from "react";
import { LoginCallback } from "@okta/okta-react";
import { useLocation } from "react-router-dom";

// LAYOUTS
import AdminLayout from "./layouts/Admin";
import AuthLayout from "./layouts/Auth";
import ViewLayout from "./layouts/View";

// VIEWS

const NotFoundView = lazy(() => import("./views/Shared/NotFoundView"));
// Login Views
const LoginView = lazy(() => import("./views/Auth/Login"));
const LoginCustomCallback = lazy(() =>
    import("./views/Auth/LoginCustomCallback")
);
// Admin Views
const ScanView = lazy(() => import("./views/Admin/Scan"));
const UserView = lazy(() => import("./views/Admin/User"));
const OrganizationView = lazy(() => import("./views/Admin/Organization"));
const UserFormView = lazy(() => import("./views/Admin/UserForm"));
const OrganizationFromView = lazy(() =>
    import("./views/Admin/OrganizationForm")
);
const ModelView = lazy(() => import("./views/Admin/Model"));
const OrganizationProfileView = lazy(() =>
    import("./views/Admin/OrganizationProfile")
);
const UserProfileView = lazy(() => import("./views/Admin/UserProfile"));
const ModelFormView = lazy(() => import("./views/Admin/ModelForm"));
const CustomModelFormView = lazy(() => import("./views/Admin/CustomModelForm"));
const LinkedModelFormView = lazy(() => import("./views/Admin/LinkedModelForm"));
const ModelParameterView = lazy(() => import("./views/Admin/ModelParameter"));
const ModelUploadView = lazy(() => import("./views/Admin/ModelUpload"));
const CloneModelView = lazy(() => import("./views/Admin/CloneModel"));

const LabStoreView = lazy(() => import("./views/Admin/LabStore"));
const LabStoreProductView = lazy(() => import("./views/Admin/LabStoreProduct"));
const MeasurementsView = lazy(() => import("./views/Admin/Measurements"));
const MaterialGroupsView = lazy(() => import("./views/Admin/MaterialGroups"));
const LotsView = lazy(() => import("./views/Admin/Lots"));
const ContactSupportView = lazy(() => import("./views/Admin/ContactSupport"));
const ActivityFeedView = lazy(() => import("./views/Admin/ActivityFeed"));
const PublishedModelsView = lazy(() => import("./views/Admin/PublishedModels"));
const PublishModelFormView = lazy(() =>
    import("./views/Admin/PublishModelForm")
);
const CouponsView = lazy(() => import("./views/Admin/Coupons"));
const PublishBundleFormView = lazy(() =>
    import("./views/Admin/PublishBundleForm")
);
const ScansDetailsView = lazy(() => import("./views/Admin/ScansDetails"));
const DashboardView = lazy(() => import("./views/Admin/Dashboard"));
const ReviewProductView = lazy(() => import("./views/Admin/ReviewProduct"));
const CommentsView = lazy(() => import("./views/Admin/Comments"));
const SecurityView = lazy(() => import("./views/Admin/Security"));
const PartnerProfileView = lazy(() => import("./views/Admin/PartnerProfile"));
const DeviceManagementView = lazy(() =>
    import("./views/Admin/DeviceManagement")
);
const SubscriptionHistoryView = lazy(() =>
    import("./views/Admin/SubscriptionHistory")
);
const PaymentsView = lazy(() => import("./views/Admin/Payments"));
const PublishedProductVersionsView = lazy(() =>
    import("./views/Admin/PublishedProductVersions")
);
const SubscribedProductView = lazy(() =>
    import("./views/Admin/SubscribedProduct")
);
const ApiKeysView = lazy(() => import("./views/Admin/ApiKeys"));
const MaterialSuppliersView = lazy(() =>
    import("./views/Admin/MaterialSuppliers")
);
const UnitsManagementView = lazy(() => import("./views/Admin/UnitsManagement"));
const PromotionCodesView = lazy(() => import("./views/Admin/PromotionCodes"));
const AnalyticsView = lazy(() => import("./views/Admin/Analytics"));
const AlertsView = lazy(() => import("./views/Admin/NeoSpectraAlerts"));
const AlertsFormView = lazy(() => import("./views/Admin/AlertsForm"));
const LablakeAlertsFormView = lazy(() =>
    import("./views/Admin/LablakeAlertsForm")
);
const ModelTransferView = lazy(() => import("./views/Admin/ModelTransfer"));
const NotificationsManagementView = lazy(() =>
    import("./views/Admin/NotificationsManagement")
);
const NotificationFormView = lazy(() =>
    import("./views/Admin/NotificationForm")
);
const ModelParameterFormView = lazy(() =>
    import("./views/Admin/ModelParameterForm")
);
const ClassifiersManagementView = lazy(() =>
    import("./views/Admin/ClassifiersManagement")
);

const MeasurementConversionConfigurationView = lazy(() =>
    import("./views/Admin/MeasurementConversionConfiguration")
);

//Lablake
const LablakeMaterialCreateView = lazy(() =>
    import("./views/Admin/LablakeMaterialCreate")
);
const LablakeMaterialsView = lazy(() =>
    import("./views/Admin/LablakeMaterials")
);
const LablakeMaterialVersionsView = lazy(() =>
    import("./views/Admin/LablakeMaterialVersions")
);
const LablakeMaterialVersionView = lazy(() =>
    import("./views/Admin/LablakeMaterialVersion")
);
const InstrumentFormView = lazy(() => import("./views/Admin/InstrumentForm"));
const InstrumentsView = lazy(() => import("./views/Admin/Instruments"));
const LablakeScansDetailsView = lazy(() =>
    import("./views/Admin/LablakeScansDetails")
);
const LablakeScansView = lazy(() => import("./views/Admin/LablakeScan"));
const LabLakeFileUploadsView = lazy(() =>
    import("./views/Admin/LablakeFileUploads")
);
const LablakeAnalyticsView = lazy(() =>
    import("./views/Admin/LablakeAnalytics")
);

// Standard Views
const ConfirmPaymentView = lazy(() => import("./views/View/ConfirmPayment"));
const LablakeAlertsView = lazy(() => import("./views/Admin/LablakeAlerts"));
// To be checked
import ScanFiltersView from "./views/Admin/ScanFilters";
import LicenseExpirationView from "./views/Admin/LicenseExpirationView";

// HELPER FUNCTIONS
import { LABSTORE_ACCESS, ROLES } from "./utils/constants";
import {
    GLOBAL_ROUTES,
    DASHBOARD_ROUTES,
    LABSTORE_ROUTES,
    MODEL_DEVELOPMENT_ROUTES,
    SCANS_MANAGEMENT_ROUTES,
    ACCESS_MANAGEMENT_ROUTES,
    LABLAKE_ROUTES,
    MATERIALS_ROUTES,
    OPTIONS_ROUTES,
    USER_MENU_ROUTES,
    PAYMENTS_ROUTES,
    AUTH_ROUTES,
} from "./utils/RoutesConstant";
import { isLabstoreAuthorized } from "./utils/RolesHelpers";

export const ROUTE_TYPES = Object.freeze({
    Route: "route",
    Navigate: "navigate",
    ProtectedRoute: "protectedRoute",
});

// !!ORDER of the routes is EXTREMLY important
export const routes = (currentUser) => {
    const location = useLocation();

    return [
        {
            exact: true,
            path: AUTH_ROUTES.LoginCallback,
            element: <LoginCallback />,
            isSecured: false,
            isLayout: false,
            type: ROUTE_TYPES.Route,
        },
        {
            exact: true,
            path: AUTH_ROUTES.CustomLoginCallback,
            element: <LoginCustomCallback />,
            isSecured: false,
            isLayout: false,
            type: ROUTE_TYPES.Route,
        },
        {
            exact: true,
            path: GLOBAL_ROUTES.LicenseExpired,
            element: <LicenseExpirationView />,
            isSecured: false,
            isLayout: false,
            type: ROUTE_TYPES.Route,
        },
        {
            exact: false,
            path: "/view",
            element: <ViewLayout />,
            isSecured: true,
            isLayout: true,
            type: ROUTE_TYPES.Route,
            subRoutes: [
                {
                    exact: true,
                    path: PAYMENTS_ROUTES.PaymentConfirmation,
                    element: <ConfirmPaymentView />,
                    type: ROUTE_TYPES.Route,
                },
            ],
        },
        {
            exact: true,
            path: "/auth",
            element: <AuthLayout />,
            isSecured: false,
            isLayout: true,
            type: ROUTE_TYPES.Route,
            subRoutes: [
                {
                    exact: true,
                    path: AUTH_ROUTES.Login,
                    element: <LoginView />,
                    type: ROUTE_TYPES.Route,
                },
            ],
        },
        {
            exact: false,
            // path: "",
            element: <AdminLayout />,
            isSecured: true,
            isLayout: true,
            type: ROUTE_TYPES.Route,
            subRoutes: [
                // SIDEMENU ITEMS
                {
                    exact: true,
                    path: DASHBOARD_ROUTES.Overview,
                    element: <DashboardView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: DASHBOARD_ROUTES.Analytics,
                    element: <AnalyticsView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: LABSTORE_ROUTES.SubscribedProducts,
                    element: <SubscribedProductView />,
                    type: ROUTE_TYPES.Route,
                },
                // Why the models view is for all?
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.ModelParameter.Get,
                    element: <ModelParameterView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.ModelParameter.Create,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    element: <ModelParameterFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.Models.Create,
                    element: <ModelFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.Models.CreateCustom,
                    element: <CustomModelFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.Models.CreateLinked,
                    element: <LinkedModelFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.Models.Upload,
                    element: <ModelUploadView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [
                        ROLES.partnerAdmin,
                        ROLES.superAdmin,
                        ROLES.support,
                    ],
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.Models.Edit,
                    element: <ModelUploadView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [
                        ROLES.partnerAdmin,
                        ROLES.superAdmin,
                        ROLES.support,
                    ],
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.Models.Clone,
                    element: <CloneModelView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.superAdmin],
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.Models.List,
                    element: <ModelView />,
                    type: ROUTE_TYPES.Route,
                },

                {
                    exact: true,
                    path: SCANS_MANAGEMENT_ROUTES.Scans.Filters,
                    element: <ScanFiltersView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: SCANS_MANAGEMENT_ROUTES.Scans.Get,
                    element: <ScansDetailsView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: SCANS_MANAGEMENT_ROUTES.Scans.List,
                    element: <ScanView />,
                    type: ROUTE_TYPES.Route,
                },
                // DROPDOWN ITEMS
                {
                    exact: true,
                    path: USER_MENU_ROUTES.Security,
                    element: <SecurityView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: USER_MENU_ROUTES.Profile,
                    element: <UserProfileView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: MATERIALS_ROUTES.MaterialGroups,
                    element: <MaterialGroupsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    exact: true,
                    path: MATERIALS_ROUTES.Lots,
                    element: <LotsView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: GLOBAL_ROUTES.Support,
                    element: <ContactSupportView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: PAYMENTS_ROUTES.Payments,
                    element: <PaymentsView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: GLOBAL_ROUTES.PageNotFound,
                    element: <NotFoundView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: LABSTORE_ROUTES.PublishedProducts.Comments,
                    element: <CommentsView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: GLOBAL_ROUTES.PartnerProfile,
                    element: <PartnerProfileView />,
                    type: ROUTE_TYPES.Route,
                },
                {
                    exact: true,
                    path: ACCESS_MANAGEMENT_ROUTES.Users.Form,
                    element: <UserFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [
                        ROLES.admin,
                        ROLES.partnerAdmin,
                        ROLES.superAdmin,
                        ROLES.support,
                    ],
                },
                {
                    exact: true,
                    path: ACCESS_MANAGEMENT_ROUTES.Users.List,
                    element: <UserView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [
                        ROLES.admin,
                        ROLES.partnerAdmin,
                        ROLES.superAdmin,
                        ROLES.support,
                    ],
                },
                {
                    exact: true,
                    path: ACCESS_MANAGEMENT_ROUTES.Affiliates.Form,
                    element: <OrganizationFromView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [
                        ROLES.partnerAdmin,
                        ROLES.superAdmin,
                        ROLES.support,
                    ],
                },
                {
                    exact: true,
                    path: ACCESS_MANAGEMENT_ROUTES.Affiliates.List,
                    element: <OrganizationView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [
                        ROLES.partnerAdmin,
                        ROLES.superAdmin,
                        ROLES.support,
                    ],
                },
                {
                    exact: true,
                    path: LABSTORE_ROUTES.PublishedProducts.Create,
                    element: <PublishModelFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                    forbidden: !isLabstoreAuthorized(
                        currentUser,
                        LABSTORE_ACCESS.publish
                    ),
                },
                {
                    exact: true,
                    path: LABSTORE_ROUTES.PublishedProducts.List,
                    element: <PublishedModelsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                    forbidden: !isLabstoreAuthorized(
                        currentUser,
                        LABSTORE_ACCESS.publish
                    ),
                },
                {
                    exact: true,
                    path: LABSTORE_ROUTES.PublishedProducts.ProductReview,
                    element: <ReviewProductView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.support],
                },
                // {
                //     exact: true,
                //     path: "products/share",
                //     element: <RevenueShareFormView />,
                //     type: ROUTE_TYPES.ProtectedRoute,
                //     allowedRoles: [ROLES.support]
                // },
                {
                    path: OPTIONS_ROUTES.Notifications.Create,
                    exact: true,
                    element: <NotificationFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.superAdmin],
                },
                {
                    path: OPTIONS_ROUTES.Notifications.List,
                    exact: true,
                    element: <NotificationsManagementView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.superAdmin],
                },
                {
                    path: USER_MENU_ROUTES.SubscriptionHistory,
                    exact: true,
                    element: <SubscriptionHistoryView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                },
                {
                    path: LABSTORE_ROUTES.Store,
                    exact: true,
                    element: <LabStoreView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden: !isLabstoreAuthorized(
                        currentUser,
                        LABSTORE_ACCESS.view
                    ),
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                },
                {
                    path: LABSTORE_ROUTES.Product,
                    exact: true,
                    element: <LabStoreProductView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden: !isLabstoreAuthorized(
                        currentUser,
                        LABSTORE_ACCESS.view
                    ),
                },
                {
                    path: ACCESS_MANAGEMENT_ROUTES.AffiliateProfile,
                    element: <OrganizationProfileView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [
                        ROLES.partnerAdmin,
                        ROLES.support,
                        ROLES.admin,
                    ],
                },
                {
                    exact: true,
                    path: MODEL_DEVELOPMENT_ROUTES.Measurements,
                    element: <MeasurementsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [
                        ROLES.superAdmin,
                        ROLES.partnerAdmin,
                        ROLES.partnerUser,
                        ROLES.admin,
                        ROLES.support,
                    ],
                },
                {
                    path: MODEL_DEVELOPMENT_ROUTES.MeasurementsConversionConfiguration,
                    element: <MeasurementConversionConfigurationView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    exact: true,
                    path: SCANS_MANAGEMENT_ROUTES.Alerts.List,
                    element: <AlertsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    path: SCANS_MANAGEMENT_ROUTES.Alerts.Create,
                    element: <AlertsFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    path: SCANS_MANAGEMENT_ROUTES.Alerts.Edit,
                    element: <AlertsFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.Alerts.List,
                    element: <LablakeAlertsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    path: LABLAKE_ROUTES.Alerts.Create,
                    element: <LablakeAlertsFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    path: LABLAKE_ROUTES.Alerts.Edit,
                    element: <LablakeAlertsFormView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    path: OPTIONS_ROUTES.ModelTransfer,
                    allowedRoles: [ROLES.support],
                    type: ROUTE_TYPES.Route,
                    element: <ModelTransferView />,
                },
                {
                    path: MATERIALS_ROUTES.MaterialsSuppliers,
                    element: <MaterialSuppliersView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.suppliersAccess,
                },
                {
                    path: OPTIONS_ROUTES.UnitsManagement,
                    allowedRoles: [ROLES.superAdmin],
                    element: <UnitsManagementView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    path: OPTIONS_ROUTES.DeviceManagement,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                    element: <DeviceManagementView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    path: OPTIONS_ROUTES.ClassifiersManagement,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    element: <ClassifiersManagementView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    exact: true,
                    path: OPTIONS_ROUTES.ApiKeys,
                    element: <ApiKeysView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden: !currentUser?.organization?.access?.apiKeyAccess,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    path: OPTIONS_ROUTES.ActivityFeed,
                    allowedRoles: [ROLES.superAdmin, ROLES.support],
                    element: <ActivityFeedView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    path: LABSTORE_ROUTES.Coupons,
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                    element: <CouponsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    path: LABSTORE_ROUTES.PublishBundle,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    element: <PublishBundleFormView />,
                    forbidden:
                        !isLabstoreAuthorized(
                            currentUser,
                            LABSTORE_ACCESS.publish
                        ) &&
                        !currentUser?.organization?.access?.createBundleAccess,
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    path: LABSTORE_ROUTES.PromoCodes,
                    allowedRoles: [
                        ROLES.support,
                        ROLES.partnerAdmin,
                        ROLES.superAdmin,
                    ],
                    element: <PromotionCodesView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                // TODO!!
                {
                    exact: true,
                    path: LABSTORE_ROUTES.PublishedProductVersion.Create,
                    element: location.state?.isBundle ? (
                        <PublishBundleFormView />
                    ) : (
                        <PublishModelFormView />
                    ),
                    allowedRoles: [ROLES.partnerAdmin, ROLES.support],
                    forbidden: !isLabstoreAuthorized(
                        currentUser,
                        LABSTORE_ACCESS.publish
                    ),
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    exact: true,
                    path: LABSTORE_ROUTES.PublishedProductVersion.Update,
                    element: location.state?.isBundle ? (
                        <PublishBundleFormView isUpdate={true} />
                    ) : (
                        <PublishModelFormView isUpdate={true} />
                    ),
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    forbidden: !isLabstoreAuthorized(
                        currentUser,
                        LABSTORE_ACCESS.publish
                    ),
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    path: LABSTORE_ROUTES.PublishedProductVersion.List,
                    element: <PublishedProductVersionsView />,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    forbidden: !isLabstoreAuthorized(
                        currentUser,
                        LABSTORE_ACCESS.publish
                    ),
                    type: ROUTE_TYPES.ProtectedRoute,
                },
                {
                    path: LABLAKE_ROUTES.Instruments.Create,
                    element: <InstrumentFormView />,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    path: LABLAKE_ROUTES.Instruments.Update,
                    element: <InstrumentFormView />,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.Instruments.List,
                    element: <InstrumentsView />,
                    allowedRoles: [
                        ROLES.partnerUser,
                        ROLES.user,
                        ROLES.admin,
                        ROLES.partnerAdmin,
                        ROLES.support,
                    ],
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.Materials.Create,
                    element: <LablakeMaterialCreateView />,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.Dashboard.Analytics,
                    element: <LablakeAnalyticsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.Materials.List,
                    element: <LablakeMaterialsView />,
                    allowedRoles: [
                        ROLES.partnerUser,
                        ROLES.user,
                        ROLES.admin,
                        ROLES.partnerAdmin,
                        ROLES.support,
                    ],
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.MaterialVersions.Create,
                    element: <LablakeMaterialVersionView />,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.MaterialVersions.Update,
                    element: <LablakeMaterialVersionView />,
                    allowedRoles: [ROLES.support, ROLES.partnerAdmin],
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.MaterialVersions.List,
                    element: <LablakeMaterialVersionsView />,
                    allowedRoles: [
                        ROLES.partnerUser,
                        ROLES.user,
                        ROLES.admin,
                        ROLES.partnerAdmin,
                        ROLES.support,
                    ],
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.Scans.List,
                    element: <LablakeScansView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.Scans.Get,
                    element: <LablakeScansDetailsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
                {
                    exact: true,
                    path: LABLAKE_ROUTES.LabLakeFileUploads,
                    element: <LabLakeFileUploadsView />,
                    type: ROUTE_TYPES.ProtectedRoute,
                    forbidden:
                        !currentUser?.organization?.access?.labLakeAccess,
                },
            ],
        },
    ];
};
